"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, BookOpen, Calendar, ChevronLeft, ChevronRight, FileText } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface Copy {
  eyebrow: string;
  heading: string;
  lead: string;
  allResourcesBtn: string;
  screenSpecsBtn: string;
  readGuide: string;
  authorTeam: string;
  defaultExcerpt: string;
  defaultCategory: string;
  errorText: string;
  retryText: string;
  noArticlesTitle: string;
  noArticlesDesc: string;
  techResourcesBtn: string;
  calcGuidesBtn: string;
  techReference: string;
  prevText: string;
  nextText: string;
  pageFormat: (current: number, total: number) => string;
}
const copyData: Record<Lang, Copy> = {
  en: {
    eyebrow: "Technical Knowledge Base",
    heading: "Engineering guides and selection articles",
    lead: "Compare wire profiles, slot openings, and flow dynamics to select the right wedge wire specification.",
    allResourcesBtn: "All technical resources",
    screenSpecsBtn: "Wedge wire screens",
    readGuide: "Read guide",
    authorTeam: "MutualFilter Engineering",
    defaultExcerpt: "Detailed technical analysis, slot dimension tolerances, and material recommendations for industrial filtration assemblies.",
    defaultCategory: "Technical Article",
    errorText: "Unable to load technical articles at this moment.",
    retryText: "Try again",
    noArticlesTitle: "No technical articles found",
    noArticlesDesc: "Explore our calculation charts and datasheets for detailed specifications.",
    techResourcesBtn: "View technical resources",
    calcGuidesBtn: "Calculation guides",
    techReference: "Technical Reference",
    prevText: "Previous",
    nextText: "Next",
    pageFormat: (current, total) => `Page ${current} of ${total}`
  },
  zh: {
    eyebrow: "技术知识库",
    heading: "工程指南与选型文章",
    lead: "对比型线截面、缝隙开孔与流体力学特性，精准选定楔形滤网规格参数。",
    allResourcesBtn: "全部技术资源",
    screenSpecsBtn: "楔形丝滤网规格",
    readGuide: "阅读指南",
    authorTeam: "MutualFilter 工程技术团队",
    defaultExcerpt: "详细技术分析、缝隙公差及工业过滤组件材质选型建议。",
    defaultCategory: "技术文章",
    errorText: "当前无法加载技术文章。",
    retryText: "重试",
    noArticlesTitle: "未找到相关技术文章",
    noArticlesDesc: "浏览我们的计算图表与技术规格书获取详细工程参数。",
    techResourcesBtn: "查看技术资源",
    calcGuidesBtn: "计算指南",
    techReference: "技术参考资料",
    prevText: "上一页",
    nextText: "下一页",
    pageFormat: (current, total) => `第 ${current} 页，共 ${total} 页`
  },
  es: {
    eyebrow: "Base de conocimientos técnicos",
    heading: "Guías de ingeniería y artículos de selección",
    lead: "Compare perfiles de alambre, aberturas de ranura y dinámica de fluidos para seleccionar la especificación adecuada.",
    allResourcesBtn: "Todos los recursos técnicos",
    screenSpecsBtn: "Rejillas de alambre de cuña",
    readGuide: "Leer guía",
    authorTeam: "Equipo de Ingeniería MutualFilter",
    defaultExcerpt: "Análisis técnico detallado, tolerancias de ranuras y recomendaciones de aleaciones para conjuntos de filtración industrial.",
    defaultCategory: "Artículo técnico",
    errorText: "No se pudieron cargar los artículos técnicos en este momento.",
    retryText: "Reintentar",
    noArticlesTitle: "No se encontraron artículos técnicos",
    noArticlesDesc: "Consulte nuestras tablas de cálculo y hojas de especificaciones para obtener parámetros detallados.",
    techResourcesBtn: "Ver recursos técnicos",
    calcGuidesBtn: "Guías de cálculo",
    techReference: "Referencia técnica",
    prevText: "Anterior",
    nextText: "Siguiente",
    pageFormat: (current, total) => `Página ${current} de ${total}`
  },
  de: {
    eyebrow: "Technische Wissensdatenbank",
    heading: "Konstruktionsleitfäden und Auswahlartikel",
    lead: "Vergleichen Sie Drahtprofile, Spaltweiten und Strömungsdynamik zur gezielten Spezifikation Ihrer Spaltsiebe.",
    allResourcesBtn: "Alle technischen Ressourcen",
    screenSpecsBtn: "Spaltsiebe",
    readGuide: "Leitfaden lesen",
    authorTeam: "MutualFilter Fachteam",
    defaultExcerpt: "Detaillierte technische Analysen, Spaltmaßtoleranzen und Werkstoffempfehlungen für industrielle Trenntechnik.",
    defaultCategory: "Technischer Fachartikel",
    errorText: "Fachartikel konnten derzeit nicht geladen werden.",
    retryText: "Erneut versuchen",
    noArticlesTitle: "Keine technischen Artikel gefunden",
    noArticlesDesc: "Nutzen Sie unsere Berechnungsdiagramme und Datenblätter für vertiefte Spezifikationsdaten.",
    techResourcesBtn: "Technische Ressourcen ansehen",
    calcGuidesBtn: "Berechnungsleitfäden",
    techReference: "Technische Referenz",
    prevText: "Zurück",
    nextText: "Weiter",
    pageFormat: (current, total) => `Seite ${current} von ${total}`
  },
  fr: {
    eyebrow: "Base de connaissances techniques",
    heading: "Guides d'ingénierie et articles de sélection",
    lead: "Comparez les profils de fils, les fentes et la dynamique des fluides pour choisir la grille profilée appropriée.",
    allResourcesBtn: "Toutes les ressources techniques",
    screenSpecsBtn: "Grilles à fils profilés",
    readGuide: "Lire le guide",
    authorTeam: "Ingénierie MutualFilter",
    defaultExcerpt: "Analyses techniques approfondies, tolérances d'ouverture et conseils métallurgiques pour vos ensembles de filtration.",
    defaultCategory: "Article technique",
    errorText: "Impossible de charger les articles techniques pour le moment.",
    retryText: "Réessayer",
    noArticlesTitle: "Aucun article technique trouvé",
    noArticlesDesc: "Consultez nos abaques de calcul et nos fiches techniques pour les paramètres d'ingénierie détaillés.",
    techResourcesBtn: "Voir les ressources techniques",
    calcGuidesBtn: "Guides de calcul",
    techReference: "Référence technique",
    prevText: "Précédent",
    nextText: "Suivant",
    pageFormat: (current, total) => `Page ${current} sur ${total}`
  },
  pt: {
    eyebrow: "Base de conhecimento técnico",
    heading: "Guias de engenharia e artigos de seleção",
    lead: "Compare perfis de arame, aberturas de ranhura e dinâmica de vazão para selecionar a especificação correta.",
    allResourcesBtn: "Todos os recursos técnicos",
    screenSpecsBtn: "Telas de arame em cunha",
    readGuide: "Ler guia",
    authorTeam: "Engenharia MutualFilter",
    defaultExcerpt: "Análise técnica detalhada, tolerâncias dimensionais e recomendações de ligas para elementos industriais.",
    defaultCategory: "Artigo técnico",
    errorText: "Não foi possível carregar os artigos técnicos no momento.",
    retryText: "Tentar novamente",
    noArticlesTitle: "Nenhum artigo técnico encontrado",
    noArticlesDesc: "Explore nossos gráficos de cálculo e fichas técnicas para parâmetros completos.",
    techResourcesBtn: "Ver recursos técnicos",
    calcGuidesBtn: "Guias de cálculo",
    techReference: "Referência técnica",
    prevText: "Anterior",
    nextText: "Próximo",
    pageFormat: (current, total) => `Página ${current} de ${total}`
  }
};
const dateLocales: Record<Lang, string> = {
  en: "en-US",
  zh: "zh-CN",
  es: "es-ES",
  de: "de-DE",
  fr: "fr-FR",
  pt: "pt-BR"
};
function ArticlesConsumer({
  lang
}: {
  lang: Lang;
}) {
  const {
    posts,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = usePosts();
  const copy = pick(lang, copyData);
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 6
      }).map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4 rounded-lg border border-border bg-card p-5">
            <Skeleton className="h-48 w-full rounded-lg" />
            <div className="flex gap-2">
              <Skeleton className="h-5 w-20 rounded-md" />
              <Skeleton className="h-5 w-24 rounded-md" />
            </div>
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <div className="mt-auto pt-4 border-t border-border flex justify-between items-center">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-8 w-24 rounded-md" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-lg border border-destructive/30 bg-card p-8 text-center">
        <p className="text-sm font-medium text-destructive mb-3">
          {copy.errorText}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          {copy.retryText}
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="rounded-lg border border-border bg-card p-12 text-center">
        <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground mb-4">
          <FileText className="size-6" />
        </div>
        <h3 className="mf-h3 text-foreground">
          {copy.noArticlesTitle}
        </h3>
        <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
          {copy.noArticlesDesc}
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <Button asChild variant="outline" size="sm">
            <Link to="/TechnicalResourcesPage">
              {copy.techResourcesBtn}
            </Link>
          </Button>
          <Button asChild size="sm">
            <Link to="/CalculationGuidesPage">
              {copy.calcGuidesBtn}
            </Link>
          </Button>
        </div>
      </div>;
  }
  return <div className="space-y-10">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, i) => {
        const categoryName = post.categories && post.categories.length > 0 ? post.categories[0].name : copy.defaultCategory;
        const localeStr = dateLocales[lang] || "en-US";
        const formattedDate = post.date ? new Date(post.date).toLocaleDateString(localeStr, {
          year: "numeric",
          month: "short",
          day: "numeric"
        }) : null;
        return <article key={post.id} data-index={i} className="flex">
              <Card className="flex flex-col w-full overflow-hidden border-border bg-card rounded-lg hover:border-primary/40 transition-[border-color] duration-200 gap-0 py-0">
                {post.featuredImage ? <div className="mf-card-media aspect-16/10 w-full">
                    <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="size-full object-cover" />
                  </div> : <div className="mf-card-media aspect-16/10 w-full flex items-center justify-center bg-muted/60 text-muted-foreground">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <BookOpen className="size-8" />
                      <span className="text-[13px] font-mono uppercase tracking-wider">
                        {copy.techReference}
                      </span>
                    </div>
                  </div>}

                <CardHeader className="gap-2.5 p-5 pb-2">
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant="outline" className="font-mono text-[13px] bg-muted text-foreground">
                      {categoryName}
                    </Badge>
                    {formattedDate && <div className="flex items-center gap-1 text-[13px] text-muted-foreground">
                        <SafeHydrate><Calendar className="size-3" /></SafeHydrate>
                        <span>{formattedDate}</span>
                      </div>}
                  </div>
                  <h3 className="mf-h3 leading-snug text-foreground line-clamp-2">
                    {post.link ? <Link to={post.link} className="hover:text-primary transition-colors">
                        {post.title}
                      </Link> : post.title}
                  </h3>
                </CardHeader>

                <CardContent className="px-5 pb-4 grow">
                  {post.excerpt ? <div className="text-sm text-muted-foreground line-clamp-3 leading-relaxed" dangerouslySetInnerHTML={{
                __html: post.excerpt
              }} /> : <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">
                      {copy.defaultExcerpt}
                    </p>}
                </CardContent>

                <CardFooter className="px-5 pt-3 pb-5 border-t border-border mt-auto flex items-center justify-between">
                  <span className="text-[13px] text-muted-foreground font-mono">
                    {copy.authorTeam}
                  </span>
                  <Button asChild variant="ghost" size="sm" className="group text-primary hover:text-primary hover:bg-accent px-2">
                    <Link to={post.link || "/TechnicalResourcesPage"}>
                      <span>{copy.readGuide}</span>
                      <ArrowRight className="size-3.5" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </article>;
      })}
      </div>

      {totalPages > 1 && <div className="flex flex-wrap items-center justify-between gap-4 border-t border-border pt-6">
          <p className="text-[13px] font-mono text-muted-foreground">
            {copy.pageFormat(currentPage, totalPages)}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrev} className="gap-1">
              <ChevronLeft className="size-4" />
              <span>{copy.prevText}</span>
            </Button>
            <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNext} className="gap-1">
              <span>{copy.nextText}</span>
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>}
    </div>;
}
export default function RelatedArticles() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  return <section id="relatedarticles" data-nav="light" className="bg-background mf-section border-t border-border">
      <div className="mf-container">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-[13px] font-mono font-semibold uppercase tracking-wider text-primary mb-3">
              <BookOpen className="size-3.5" aria-hidden="true" />
              <span>{copy.eyebrow}</span>
            </div>
            <h2 className="mf-h2 text-foreground">
              {copy.heading}
            </h2>
            <p className="mt-3 mf-lead text-muted-foreground">
              {copy.lead}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button asChild variant="outline">
              <Link to="/TechnicalResourcesPage">
                {copy.allResourcesBtn}
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/WedgeWireScreenPage">
                {copy.screenSpecsBtn}
              </Link>
            </Button>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }} className="w-full">
          <ArticlesConsumer lang={lang} />
        </PostsProvider>
      </div>
    </section>;
}